import { useState } from "react";
import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

import {
  PercentOrMoneyInput,
  type PercentOrMoneyMode,
} from "@krea/shared/design-system/input/percent-or-money-input";
import type { Currency } from "@krea/shared/types/common";

import type { IRevolvingCreditBidInput } from "@/features/lender-responses/types/lender-response";
import type { BidPostData } from "@/validations";

interface IWithdrawalInputProps {
  control: Control<BidPostData<IRevolvingCreditBidInput>>;
  bidFormIndex: number;
  currency: Currency;
  disabled?: boolean;
}

export const WithdrawalInput = ({
  control,
  bidFormIndex,
  currency,
  disabled = false,
}: IWithdrawalInputProps) => {
  const {
    field: { onChange: onChangeFee, value: feeValue },
  } = useController({
    name: `bids.${bidFormIndex}.withdrawalFee`,
    control,
    // The exactly-one rule is an object-level test: re-validate the sibling
    // so its error clears once this field satisfies the rule.
    rules: { deps: [`bids.${bidFormIndex}.withdrawalFeeAmount`] },
  });

  const {
    field: { onChange: onChangeAmount, value: amountValue },
  } = useController({
    name: `bids.${bidFormIndex}.withdrawalFeeAmount`,
    control,
    rules: { deps: [`bids.${bidFormIndex}.withdrawalFee`] },
  });

  const [mode, setMode] = useState<PercentOrMoneyMode>(
    amountValue != null ? "amount" : "percent",
  );

  const handleModeChange = (next: PercentOrMoneyMode) => {
    setMode(next);
    if (next === "percent") {
      onChangeAmount(null);
    } else {
      onChangeFee(null);
    }
  };

  const inputValue: number | null =
    mode === "amount" ? (amountValue ?? null) : (feeValue ?? null);

  const handleChange = (n: number | null) => {
    if (mode === "amount") {
      onChangeAmount(n);
    } else {
      onChangeFee(n);
    }
  };

  return (
    <PercentOrMoneyInput
      id={`withdrawal-${bidFormIndex}`}
      value={inputValue}
      onChange={handleChange}
      mode={mode}
      onModeChange={handleModeChange}
      currency={currency}
      disabled={disabled}
    />
  );
};
